import gql from 'graphql-tag';

export const VotingAreaFields = gql`
  fragment VotingAreaFields on ri_apps_prd_voting_areas {
    voting_area_id
    organization_id
    state_id
    voting_areas_state {
      state_id
      name: short_name
    }
    city_id
    voting_areas_city {
      city_id
      name
    }
    county_id
    voting_areas_county {
      county_id
      name
    }
    precinct_id
    voting_areas_precinct {
      pricinct_id
      name
    }
    state_leg_lower_district_id
    voting_areas_state_leg_lower_district {
      state_leg_lower_district_id
      name
      district_id
    }
    state_leg_lower_sub_district_id
    voting_areas_state_leg_lower_sub_district {
      state_leg_lower_sub_district_id
      name
      district_id
    }
    state_leg_upper_district_id
    voting_areas_state_leg_upper_district {
      state_leg_upper_district_id
      name
      district_id
    }
    congressional_district_id
    voting_areas_congressional_district {
      congressional_district_id
      district_id
    }
    city_council_id
    voting_areas_city_council {
      city_council_id
      state_id
      city_id
      code
      name
    }
    neighborhood_id
    county_council
    county_council_district
    county_commissioner
    county_commissioner_district
    justice_of_the_peace_district
    school_district
    ward
    advisory_neighborhood_district
    jurisname
    school_board
    precinctcode
  }
`;

export const GET_VOTING_AREAS = gql`
  query getVotingAreas($orgId: Int!) {
    rows: ri_apps_prd_organization_voting_areas(
      where: { organization_id: { _eq: $orgId } }
      order_by: [{ organization_voting_areas_voting_area: { state_id: asc_nulls_last } }]
    ) {
      organization_voting_areas_voting_area {
        ...VotingAreaFields
      }
    }
  }
  ${VotingAreaFields}
`;

/**
 * Just the states an organization operates in, from its voting areas.
 *
 * Exists because `voters_comb` is a UNION ALL view over per-state child tables: querying it
 * on `voter_id` alone reads every state in the union. Any voter lookup has to carry a state
 * predicate, and for rows that do not record one of their own this is where it comes from.
 */
export const GET_ORGANIZATION_STATES = gql`
  query GetOrganizationStates($orgId: Int!) {
    rows: ri_apps_prd_organization_voting_areas(where: { organization_id: { _eq: $orgId } }) {
      organization_voting_areas_voting_area {
        voting_areas_state {
          short_name
        }
      }
    }
  }
`;

export const UPSERT_VOTING_AREAS = gql`
  mutation upsertVotingAreas($data: [ri_apps_prd_voting_areas_insert_input!]!) {
    rows: insert_ri_apps_prd_voting_areas(
      objects: $data
      on_conflict: {
        constraint: voting_areas_pkey
        update_columns: [
          city_id
          congressional_district_id
          county_id
          precinct_id
          state_id
          state_leg_lower_district_id
          state_leg_lower_sub_district_id
          state_leg_upper_district_id
          city_council_id
          neighborhood_id
          county_council
          county_council_district
          county_commissioner
          county_commissioner_district
          justice_of_the_peace_district
          school_district
          ward
          advisory_neighborhood_district
          jurisname
          school_board
          precinctcode
        ]
      }
    ) {
      returning {
        ...VotingAreaFields
      }
    }
  }
  ${VotingAreaFields}
`;

// Lightweight variant of UPSERT_VOTING_AREAS that returns only voting_area_id.
// Use this where callers only need the ids (e.g. to link org/task voting areas);
// it avoids resolving the deeply-nested VotingAreaFields tree on every upsert.
export const UPSERT_VOTING_AREAS_IDS = gql`
  mutation upsertVotingAreas($data: [ri_apps_prd_voting_areas_insert_input!]!) {
    rows: insert_ri_apps_prd_voting_areas(
      objects: $data
      on_conflict: {
        constraint: voting_areas_pkey
        update_columns: [
          city_id
          congressional_district_id
          county_id
          precinct_id
          state_id
          state_leg_lower_district_id
          state_leg_lower_sub_district_id
          state_leg_upper_district_id
          city_council_id
          neighborhood_id
          county_council
          county_council_district
          county_commissioner
          county_commissioner_district
          justice_of_the_peace_district
          school_district
          ward
          advisory_neighborhood_district
          jurisname
          school_board
          precinctcode
        ]
      }
    ) {
      returning {
        voting_area_id
      }
    }
  }
`;

export const DELETE_VOTING_AREAS = gql`
  mutation deleteVotingAreas($ids: [Int!]!) {
    rows: delete_ri_apps_prd_voting_areas(where: { voting_area_id: { _in: $ids } }) {
      affected_rows
    }
  }
`;

export const DELETE_VOTING_AREAS_BY_ORG_ID = gql`
  mutation deleteVotingAreas($org_id: Int!) {
    rows: delete_ri_apps_prd_voting_areas(where: { organization_id: { _eq: $org_id } }) {
      affected_rows
    }
  }
`;

export const UPSERT_ORGANIZATION_VOTING_AREAS = gql`
  mutation upsertOrganizationVotingAreas(
    $data: [ri_apps_prd_organization_voting_areas_insert_input!]!
  ) {
    rows: insert_ri_apps_prd_organization_voting_areas(
      objects: $data
      on_conflict: { constraint: organization_voting_areas_pkey, update_columns: [] }
    ) {
      affected_rows
    }
  }
`;
